// Human-friendly timestamp formatting for the gallery. `formatRelative` gives a
// compact "added N ago" for recent media and falls back to an absolute date for
// older items; `formatAbsolute` is the full local datetime used in tooltips. Both
// are pure with an injectable clock so they unit-test deterministically.

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/**
 * Compact relative time, e.g. "just now", "5 min ago", "3 hr ago", "2 days ago".
 * Items a week or older show an absolute short date instead ("Jun 20, 2026").
 * Returns "" for a missing/invalid timestamp. Future times (clock skew) read as
 * "just now" rather than a negative interval.
 */
export function formatRelative(ms: number, nowMs: number = Date.now()): string {
  if (!Number.isFinite(ms) || ms <= 0) return "";
  const diff = nowMs - ms;
  if (diff < MINUTE) return "just now";
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)} min ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)} hr ago`;
  if (diff < WEEK) {
    const days = Math.floor(diff / DAY);
    return days === 1 ? "1 day ago" : `${days} days ago`;
  }
  return new Date(ms).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Full local datetime for tooltips, e.g. "6/20/2026, 12:53:00 PM". "" if invalid. */
export function formatAbsolute(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "";
  return new Date(ms).toLocaleString();
}
